import type { DocHandle } from "@automerge/automerge-repo";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from "@tanstack/react-router";
import { mintUlid } from "den/thread-feed";
import { blobHref } from "forest/file-tree/blob-href";
import { DOCUMENT_KIND } from "forest/file-tree/file-kinds";
import { FileTree } from "forest/file-tree/FileTree";
import { useTreeExpansion } from "forest/file-tree/use-expansion";
import { useFileCommands } from "forest/file-tree/use-file-commands";
import { useHiddenFiles } from "forest/file-tree/use-hidden-files";
import { useMaterializedFiles } from "forest/file-tree/use-materialized-files";
import { launchableApps, useAppIndex } from "forest/sandbox/use-app-index";
import {
  useUploadFiles,
  type UploadFn,
  type UploadOutcome,
} from "forest/uploads/use-upload-files";
import {
  Building2,
  ChevronsLeft,
  ChevronsUpDown,
  FilePlus,
  FileUp,
  FolderTree,
  LayoutGrid,
  Loader2,
  LogOut,
  MessageSquare,
  MessageSquarePlus,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PinOff,
  Plus,
  RefreshCw,
  Settings,
  UserRound,
} from "lucide-react";
import type { AppIndexEntry } from "playground/apps/index/app-index";
import { createMarkdownFile } from "playground/hal/file-ops";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { invariant } from "ts-invariant";

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { enterPath } from "../../shared/origins";
import { flattenUserVisibleText } from "../../shared/workspace-context";
import { AppIcon } from "../apps/display";
import { useRecentApps } from "../apps/use-recent-apps";
import { signOut, useSession } from "../auth-client";
import { SyncIndicator } from "../automerge/SyncIndicator";
import { usePinned } from "../automerge/use-pinned";
import { usePlayground } from "../automerge/use-playground";
import { useProjectDoc } from "../automerge/use-project-doc";
import { useRepoMaybe } from "../automerge/use-repo-maybe";
import type { ChatMessage } from "../chats/api";
import { AppConsentAsksProvider } from "../chats/app-consent-asks";
import { ChatActionsMenu } from "../chats/ChatActionsMenu";
import type { ChatSurfaceMode } from "../chats/ChatSurface";
import { useChatList, useOpenChat } from "../chats/use-chats";
import { RootCheckpointControls } from "../components/checkpoints-panel";
import { FileActionsMenu } from "../components/file-actions-menu";
import { InstallButton } from "../components/InstallButton";
import {
  WORKSPACE_RAIL_WIDTH,
  WorkspaceRail,
  workspacePanelTabLabel,
  type WorkspacePanelTab,
} from "../components/WorkspaceRail";
import { SendFeedbackDialog } from "../feedback/SendFeedbackDialog";
import { useOnline } from "../hooks/use-online";
import { usePanelOpen } from "../hooks/use-panel-open";
import { TenantNotFoundError, useIdentity, type Identity } from "../identity";
import { IntegrationConnectProvider } from "../integrations/connect";
import { useOnboardingState } from "../onboarding/use-onboarding-state";
import { colorForSlug, type PresenceUser } from "../presence/presence";
import { PresenceStack } from "../presence/PresenceStack";
import { SandboxProvider } from "../sandbox/SandboxProvider";
import { useBuiltinResync } from "../sandbox/use-builtin-resync";
import { CommandPalette } from "../search/CommandPalette";
import { useTenants } from "../tenants";
import { PendingPane } from "./-pending";
import { Redirect } from "./-redirect";
import { TenantNotFound } from "./-tenant-not-found";
import {
  useWorkspace,
  WorkspaceContext,
  type WorkspaceContextValue,
} from "./-workspace-context";

// Lazy: the chat stack (Streamdown + ai SDK) only renders once a chat is in
// scope — statically imported it sat on the layout chunk's boot path.
const ChatSurface = lazy(() =>
  import("../chats/ChatSurface").then((m) => ({ default: m.ChatSurface })),
);

export interface WorkspaceSearch {
  chat?: string;
}

/** Stable empty file map so the materialize hook doesn't re-run pre-load. */
const EMPTY_FILES: Record<string, never> = {};

/**
 * Shell-shaped skeleton shown while the project doc + playground come up (after
 * auth/onboarding have already resolved). Mirrors the real header + a content
 * pane so the layout doesn't jump when the workspace renders.
 */
function WorkspaceShellSkeleton() {
  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border pr-4">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center",
            WORKSPACE_RAIL_WIDTH,
          )}
        >
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
        <Skeleton className="h-6 w-24" />
        <div className="flex-1" />
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-8 w-24 rounded-md" />
      </header>
      <PendingPane />
    </div>
  );
}

export const Route = createFileRoute("/_workspace")({
  validateSearch: (search: Record<string, unknown>): WorkspaceSearch => ({
    chat: typeof search.chat === "string" ? search.chat : undefined,
  }),
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const { data: session, isPending } = useSession();
  // The page being visited, captured so a logged-out visitor returns here after
  // signing in rather than landing on the workspace home. `href` is the relative
  // path + search + hash (no origin) — exactly the `next` the sign-in page and
  // OAuth flow accept as a safe same-origin path. Read live so a session that
  // drops mid-session returns to wherever the user actually was, not their
  // entry page. `<Redirect>` fires the sign-in navigation once, so this can't
  // wrap the pending `/sign-in?next=…` URL into its own `next` and run away —
  // see `-redirect.tsx` for why the naive `<Navigate>` livelocked.
  const attemptedHref = useLocation().href;
  const onboarding = useOnboardingState();
  // The single workspace-wide identity gate (SUP-149): resolve `/api/whoami`
  // here so `tenant`/`ownerSlug` are non-null everywhere below the shell.
  // Agent-name hooks read it from `WorkspaceContext` and never bind to a
  // placeholder name while it's in flight.
  const identity = useIdentity();
  // Null until the wasm compile settles (automerge/repo.ts) — tens of ms
  // after first paint.
  const repo = useRepoMaybe();

  const loadingScreen = (
    <div className="flex h-dvh items-center justify-center text-muted-foreground">
      Loading…
    </div>
  );
  // Session first: until it resolves we can't decide anything else.
  if (isPending) return loadingScreen;
  if (!session)
    return <Redirect to="/sign-in" search={{ next: attemptedHref }} />;
  // Signed in, but this subdomain isn't a tenant we can reach — either it doesn't
  // exist or we're not a member (both surface as a whoami 404 →
  // TenantNotFoundError; the worker keeps them indistinguishable so membership
  // never leaks). Decide this the moment identity settles — it fails fast
  // (retry:false) — rather than waiting on the onboarding query, which for a
  // non-member also 404s and would otherwise hold us on "Loading…" through its
  // retry backoff. Checked AFTER the sign-in gate on purpose: a logged-out
  // visitor is sent to sign-in above and never learns whether the tenant exists
  // (the worker 404s an unknown tenant only for authenticated callers).
  if (identity.error instanceof TenantNotFoundError) return <TenantNotFound />;
  // Now block on the workspace's own data (onboarding flag + resolved identity).
  if (onboarding.isLoading || identity.isPending) return loadingScreen;
  // Fail closed: missing data must not slip into <WorkspaceShell>, which assumes
  // the onboarding baseline (.agents/, AGENTS.md, etc.) is in place and a
  // resolved identity. We key off data presence rather than `.error` so a
  // *background* revalidation failure (offline foreground, cold DO) renders the
  // last-known state instead of tearing the shell down — render from cache,
  // revalidate quietly. With nothing cached, this is still the genuine-failure
  // gate, since the loading gate above only clears once the queries settle.
  if (!onboarding.data || !identity.data) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
        <p>Could not load workspace status.</p>
        <button
          type="button"
          onClick={() => {
            void onboarding.refetch();
            void identity.refetch();
          }}
          className="rounded-md border border-border px-3 py-1 hover:bg-muted hover:text-foreground"
        >
          Retry
        </button>
      </div>
    );
  }
  if (!onboarding.data.onboarded) return <Redirect to="/onboarding" />;
  // Same skeleton as the shell's own gate — no intermediate flash.
  if (!repo) return <WorkspaceShellSkeleton />;

  return (
    <WorkspaceShell
      identity={identity.data}
      authorEmail={session.user.email}
      // Prefer the name set on `/me` (registry, via whoami) over the IdP name in
      // the session cookie, so a name change shows in the menu + presence.
      authorName={identity.data.name ?? session.user.name ?? null}
      authorImage={session.user.image ?? null}
    />
  );
}

function WorkspaceShell({
  identity,
  authorEmail,
  authorName,
  authorImage,
}: {
  identity: Identity;
  authorEmail: string;
  authorName: string | null;
  authorImage: string | null;
}) {
  const navigate = useNavigate();
  const online = useOnline();
  const author = `user:${authorEmail}`;
  const { url: projectUrl, handle, doc, isLoading, error } = useProjectDoc();
  // Stable self-identity for presence (memoized so we don't re-broadcast it on
  // every render). Other people in the workspace stack their avatars beside the
  // user menu; this user is excluded from their own stack.
  const presenceSelf = useMemo<PresenceUser>(
    () => ({
      ownerSlug: identity.ownerSlug,
      name: authorName,
      image: authorImage,
      email: authorEmail,
      color: colorForSlug(identity.ownerSlug),
    }),
    [identity.ownerSlug, authorName, authorImage, authorEmail],
  );
  // Gate playground construction on the project doc being ready (`doc` is only
  // set once the handle is ready). `createPlayground` does its own
  // `repo.find(root)`, which rejects (and throws unhandled) while the
  // freshly-created doc is still syncing in. Once `doc` is set the shared
  // handle is ready, so that find resolves instantly.
  const { playground } = usePlayground(
    doc ? projectUrl : undefined,
    identity.localBuiltins,
  );
  // Augment the doc tree with mounted units' children so the file browser shows
  // managed content (mounts hold no children in the doc — they're materialized
  // from the unit's index.json on the public bucket).
  // Which directories are open. Held here, not in the tree, because it is what
  // decides which mounts get listed at all.
  const expansion = useTreeExpansion();
  const treeFiles = useMaterializedFiles(
    doc?.files ?? EMPTY_FILES,
    playground,
    expansion.expanded,
  );
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const halUpload = useCallback<UploadFn>(
    async (filename, file, contentType) => {
      // Closes over `playground` defined before the loading gate below.
      // The shell only renders children once playground is constructed, so
      // any UploadFn caller is downstream of the gate — but this callback
      // is defined upstream of it, so the closure carries a nullable type
      // even though it can't fire while null. Assert to flip the type.
      invariant(playground, "playground not ready");
      return playground.upload(filename, file, contentType);
    },
    [playground],
  );

  const {
    uploadFiles,
    isUploading,
    pendingOverwrite,
    error: uploadError,
    clearError,
    confirmOverwrite,
    cancelOverwrite,
  } = useUploadFiles({
    upload: halUpload,
    fileMap: doc?.files ?? {},
  });

  const handleUploadClick = () => {
    clearError();
    uploadInputRef.current?.click();
  };

  // Wrap an upload pass with a single toast that walks loading → success /
  // error. The hook can also resolve with status: "prompted" (a conflict needs
  // confirmation) or "skipped" (nothing to do); in both cases the toast goes
  // away because the upload hasn't actually happened.
  const runUploadWithToast = useCallback(
    (count: number, start: () => Promise<UploadOutcome>) => {
      if (count === 0) return;
      const toastId = toast.loading(
        count === 1 ? "Uploading file…" : `Uploading ${count} files…`,
      );
      void start().then((outcome) => {
        if (outcome.status === "uploaded") {
          const n = outcome.filenames.length;
          toast.success(n === 1 ? "Uploaded file" : `Uploaded ${n} files`, {
            id: toastId,
          });
        } else if (outcome.status === "error") {
          toast.error(outcome.message, { id: toastId });
        } else {
          // "prompted" hands off to the overwrite confirm; "skipped" is a
          // no-op. Either way the loading toast should go.
          toast.dismiss(toastId);
        }
      });
    },
    [],
  );

  const handleUploadInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Reset the input so picking the same file twice in a row still fires.
    e.target.value = "";
    runUploadWithToast(picked.length, () => uploadFiles(picked));
  };

  const handleConfirmOverwrite = () => {
    const count = pendingOverwrite?.files.length ?? 0;
    runUploadWithToast(count, () => confirmOverwrite());
  };

  // Drop target for the FileTree area — appears as an overlay only while
  // a drag is active; otherwise invisible and click-through.
  const {
    getRootProps: getDropRootProps,
    getInputProps: getDropInputProps,
    isDragActive,
  } = useDropzone({
    disabled: isUploading,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: (picked) => {
      runUploadWithToast(picked.length, () => uploadFiles(picked));
    },
  });
  const pinned = usePinned(author);
  // The project list has two hosts: the wide shell's panel beside its icon
  // rail (`panel`, persisted), and the narrow shell's Sheet (`filesOpen`,
  // reset on every navigation).
  const [filesOpen, setFilesOpen] = useState(false);
  const panel = usePanelOpen();
  const [fileTab, setFileTab] = useState<WorkspacePanelTab>("files");
  // Dot-entries are hidden by default, with the OS chords (⌘⇧. / Ctrl+H) as the
  // reveal. One hook per shell: the listener is global.
  const { showHidden: showHiddenFiles, toggle: toggleHiddenFiles } =
    useHiddenFiles();
  const location = useLocation();
  useEffect(() => {
    setFilesOpen(false);
  }, [location.href]);
  // Tracks the chat id that should grab focus on mount — set by the
  // "New chat" handler so the freshly-created chat opens with the composer
  // focused (which also pops the mobile dock open).
  const [focusOnMountChatId, setFocusOnMountChatId] = useState<string | null>(
    null,
  );
  const openBlankChat = useOpenChat();
  // Measure the shell (a ResizeObserver — the container-query equivalent for
  // the JS layout switches a CSS `@container` can't express). `compact`/`isWide`
  // are derived from it below, once the chat's in-scope state is known.
  const [shellRef, shellWidth] = useElementWidth();

  const search = Route.useSearch();

  // Which route is active determines where the chat lives (full page vs side
  // pane) and what "open file"/"close file"/"close chat" should do.
  const filesMatch = useMatch({
    from: "/_workspace/files/$",
    shouldThrow: false,
  });
  const chatRouteMatch = useMatch({
    from: "/_workspace/chats/$chatId",
    shouldThrow: false,
  });
  const activeFile = filesMatch?.params._splat ?? null;
  const routeChatId = chatRouteMatch?.params.chatId ?? null;
  // Content is on screen for any view that isn't the full-page chat or the home
  // launcher — i.e. not a chat route and the path is deeper than "/". Opening a
  // chat then rides alongside it (side pane) instead of replacing it; on the
  // bare home view the chat takes the full page.
  const hasContent = routeChatId === null && location.pathname !== "/";

  // Document tabs. Every file visited — markdown and code alike — stays open
  // as a tab until it's individually closed or the whole editor surface is
  // closed; the files route swaps TipTap/Monaco per tab. The list lives here
  // in the shell so it survives the editor unmounting (e.g. while a full-page
  // chat has the main pane). The focused tab is whatever the route points at;
  // this list is only membership+order. `storedTabs` is the persisted
  // membership; `editorTabs` — what everything consumes — is derived to
  // always contain the focused file, so a freshly-visited path is a tab
  // even before the persistence effect below commits it.
  const [storedTabs, setStoredTabs] = useState<string[]>([]);
  const editorTabs =
    activeFile && !storedTabs.includes(activeFile)
      ? [...storedTabs, activeFile]
      : storedTabs;
  useEffect(() => {
    if (!activeFile) return;
    setStoredTabs((prev) =>
      prev.includes(activeFile) ? prev : [...prev, activeFile],
    );
  }, [activeFile]);
  // The app index resolves every installed App — built-in mounts and workspace
  // directories alike — with display metadata (name/icon/manifest) already
  // denormalized onto each entry. Subscribed once at the shell and shared via
  // context so every surface reads the same reactive list.
  const apps = useAppIndex(playground);
  const recentApps = useRecentApps(identity.tenant);

  // The chat in scope is whichever chat is currently associated with the
  // user's view — the side pane (?chat=) on a file/home route, OR the
  // full-page /chats/$chatId route. Used to carry the chat along when the
  // user opens a different file or closes the current file.
  const sideChatId = search.chat ?? null;
  const chatInScope = sideChatId ?? routeChatId;

  // A chat is "full page" when the full-page /chats/$chatId route is active;
  // otherwise an in-scope chat (?chat=) rides along as the side pane / dock.
  const isChatFullPage = routeChatId !== null;

  // The shell degrades one column at a time as it narrows, so opening a chat
  // never slams the whole layout into the mobile view — it just gives up the
  // next thing that no longer fits:
  //   1. sidebar pinned + content + chat panel   (isWide)
  //   2. content + chat panel, sidebar behind its toggle
  //   3. content + docked chat, sidebar behind its toggle   (chatDocked)
  //   4. genuinely narrow shell → compact mobile chrome     (compact)
  const sideChat = chatInScope !== null && !isChatFullPage;
  // Dock the chat only when content + panel can't fit even after the sidebar
  // has been given up. Above that it stays a side panel and the sidebar
  // collapses first. Depends only on shell width, so it can't oscillate.
  const chatDocked =
    sideChat && shellWidth < CONTENT_MIN_WIDTH + CHAT_PANEL_WIDTH;
  const chatPanelWidth = sideChat && !chatDocked ? CHAT_PANEL_WIDTH : 0;
  // Pin the project list only when it fits beside the content (and the chat
  // panel, when shown) — so it collapses to a toggle before the chat docks, and
  // pins sooner when no chat is competing for the width.
  const isWide =
    shellWidth >= SIDEBAR_WIDTH + CONTENT_MIN_WIDTH + chatPanelWidth;
  // Compact (mobile) chrome is about a genuinely narrow *shell*, not an open
  // chat — the sidebar/dock degradation above already absorbs the chat's width.
  const compact = shellWidth < COMPACT_MAX_WIDTH;

  // Reveal the project list on the host that is on screen, and only that one:
  // each host's close clears its own flag, so touching the other's here would
  // let a resize resurrect a list the user had closed.
  const setPanelOpen = panel.setOpen;
  const revealPanel = useCallback(
    (tab: WorkspacePanelTab) => {
      if (isWide) setPanelOpen(true);
      else setFilesOpen(true);
      setFileTab(tab);
    },
    [isWide, setPanelOpen],
  );

  // Which surface the single persistent <ChatSurface> renders as. One instance
  // covers all of them (keyed by chatInScope, kept at one tree slot below), so
  // switching surfaces only flips `mode` — the agent connection and message
  // list survive instead of each surface owning a separate doc subscription that
  // splits messages until a refresh. Side pane → "panel"; docked → bottom-sheet
  // "dock"; full-page → "page".
  const chatMode: ChatSurfaceMode | null = !chatInScope
    ? null
    : isChatFullPage
      ? "page"
      : chatDocked
        ? "dock"
        : "panel";

  const openFile = (path: string) => {
    // Close the mobile Files sheet on any open. Navigating to a *different*
    // page also closes it via the location effect, but a click that targets
    // the page already on screen doesn't change the URL — so close here to
    // cover the "navigate back to where you already are" case too.
    setFilesOpen(false);
    // An uploaded blob (a photo, a PDF, a video) has no automerge doc to load,
    // so there's nothing here that can edit it — pop it in a new tab and let the
    // browser render or download it natively. `blobHref` is the shared rule;
    // this used to test `s3://` alone, which is what Bear's API mints but never
    // what hearth's own presigned uploads write, so every blob landed in Monaco.
    // The tree opens blobs as links; from anywhere else the lookup runs after
    // the click, and a browser may block a tab opened that late.
    invariant(playground, "playground not ready");
    void playground.stat(path).then((inode) => {
      const href = inode && blobHref(inode);
      if (href) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      navigate({
        to: "/files/$",
        params: { _splat: path },
        // Carry any open chat along so opening a document never closes it. From a
        // full-page chat that means moving its id to the ?chat= query param;
        // replace the URL there so the chat "moves" in place rather than pushing a
        // separate full-page-chat entry onto the history stack.
        search: chatInScope ? { chat: chatInScope } : {},
        replace: isChatFullPage,
      });
    });
  };

  // Open the history route for a file or directory, closing the Files panel.
  // The project root is the empty splat (`/history`).
  const openHistory = (target: string) => {
    setFilesOpen(false);
    navigate({
      to: "/history/$",
      params: { _splat: target === "/" ? "" : target },
      search: {},
    });
  };

  const closeFile = () => {
    if (chatInScope) {
      navigate({
        to: "/chats/$chatId",
        params: { chatId: chatInScope },
        search: {},
      });
    } else {
      navigate({ to: "/", search: {} });
    }
  };

  // Evict every open tab that fails `survives`. If the focused file is among
  // the evicted, hand focus to the surviving neighbor that slid into its slot
  // (or the new last tab); with none left, leave the editor entirely, same as
  // the title-bar close. The single tab-removal path — individual close,
  // close-all, and delete eviction all go through here.
  const evictTabs = (survives: (path: string) => boolean) => {
    const next = editorTabs.filter(survives);
    setStoredTabs(next);
    if (!activeFile || survives(activeFile)) return;
    const idx = editorTabs.indexOf(activeFile);
    const fallback =
      next[
        Math.min(
          editorTabs.slice(0, idx).filter(survives).length,
          next.length - 1,
        )
      ];
    if (fallback === undefined) {
      closeFile();
      return;
    }
    navigate({
      to: "/files/$",
      params: { _splat: fallback },
      search: chatInScope ? { chat: chatInScope } : {},
    });
  };

  const closeEditorTab = (path: string) => {
    // `editorTabs` always contains the focused file (derived above), so any
    // tab the strip can render a close button for is present here.
    invariant(editorTabs.includes(path), "closing a tab that isn't open");
    evictTabs((t) => t !== path);
  };

  // The title-bar close for the editor surface: explicit close evicts every tab.
  const closeAllEditorTabs = () => evictTabs(() => false);

  // Close the side-pane chat: stay on the current route, just drop the search
  // (the ?chat= param). Works the same wherever the pane is open.
  const closeChatPane = () => {
    navigate({ to: location.pathname, search: {} });
  };

  // Back/close for the full-page chat: return to the pinboard.
  const goHome = () => navigate({ to: "/", search: {} });

  // Open an App's full-page view (by its directory path), recording the open
  // as the most recent use so the home launcher row and palette order it
  // first. Carries any open chat across the navigation (same pattern openFile
  // uses).
  const openApp = (path: string) => {
    setFilesOpen(false);
    recentApps.recordAppUse(path);
    navigate({
      to: "/app/$",
      params: { _splat: path },
      // Same as openFile: keep the open chat, moving a full-page chat into the
      // ?chat= query param and replacing the URL so it doesn't close.
      search: chatInScope ? { chat: chatInScope } : {},
      replace: isChatFullPage,
    });
  };

  const openChat = (id: string) => {
    setFilesOpen(false);
    // With a document or App on screen, open the chat as the side pane so it
    // doesn't close the content; on a bare view (home, another chat) let it take
    // the full page.
    if (hasContent) {
      navigate({ to: location.pathname, search: { chat: id } });
    } else {
      navigate({ to: "/chats/$chatId", params: { chatId: id }, search: {} });
    }
  };

  // A navigation, not a request: the id is minted here and the chat is
  // written by its first message (`chats/new-chat.ts`).
  const newChat = () => {
    const id = mintUlid();
    openBlankChat(id);
    setFocusOnMountChatId(id);
    openChat(id);
  };

  // How the shell puts itself back together after a file mutation, wherever it
  // came from: the tree's row menu, the Files panel, or the ⌘K palette. The
  // shared file commands fire these, so no call site has to remember to.
  const fileMutated: WorkspaceContextValue["fileMutated"] = {
    // Open what was just created.
    created: (path) => openFile(path),
    // A deleted file (or anything under a deleted folder) can't stay open as a
    // tab; if the focused file is among them, this hands focus on (or leaves
    // the editor), like any other tab close.
    deleted: (path) =>
      evictTabs((t) => t !== path && !t.startsWith(`${path}/`)),
    // Renamed files keep their tab — and focus — under the new path; a folder
    // rename carries every tab beneath it. Dedupe defensively (a rename onto
    // an already-open path would otherwise render two identical tabs).
    renamed: (from, to) => {
      const rewrite = (t: string) =>
        t === from
          ? to
          : t.startsWith(`${from}/`)
            ? `${to}${t.slice(from.length)}`
            : t;
      setStoredTabs((prev) => {
        const mapped = prev.map(rewrite);
        return mapped.filter((t, i) => mapped.indexOf(t) === i);
      });
      if (activeFile && rewrite(activeFile) !== activeFile) {
        navigate({
          to: "/files/$",
          params: { _splat: rewrite(activeFile) },
          search: chatInScope ? { chat: chatInScope } : {},
          // The old path no longer exists; don't leave it in the history stack.
          replace: true,
        });
      }
    },
  };

  // The one naming/CRUD layer both products run (forest). Held here so every
  // surface shares ONE inline-edit session: a rename started from a row menu
  // has to render in the tree the shell owns.
  const commands = useFileCommands(playground, fileMutated);

  // A naming session has to be visible to be typed into, and any surface can
  // start one (row menu, panel button, ⌘K). So the shell reveals it (panel,
  // tab, and the directory it lands in) rather than each caller remembering.
  const editKey = commands.edit?.key ?? null;
  const editDir = commands.edit?.dir ?? null;
  const revealDir = expansion.reveal;
  useEffect(() => {
    if (editDir === null) return;
    revealPanel("files");
    revealDir(editDir);
  }, [editKey, editDir, revealDir, revealPanel]);

  const handleSaveSideChat = async (
    _chatId: string,
    title: string,
    messages: ChatMessage[],
  ) => {
    const slug =
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40) || "chat";
    const date = new Date().toISOString().slice(0, 10);
    const defaultPath = `chats/${date}-${slug}.md`;
    const target = window.prompt("Save to workspace as", defaultPath);
    if (!target) return;
    const body = messages
      .map((m) => {
        const text = flattenUserVisibleText(m).trim();
        return text ? `**${m.role}**: ${text}` : null;
      })
      .filter((s): s is string => s !== null)
      .join("\n\n");
    const md = `# ${title}\n\n${body}\n`;
    try {
      invariant(playground, "playground not ready");
      await createMarkdownFile(
        { playground },
        { path: target, initialContent: md },
      );
      openFile(target);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Failed to save chat");
    }
  };

  if (isLoading || !playground) {
    return <WorkspaceShellSkeleton />;
  }
  if (error || !doc) {
    // This renders instead of the shell, so it unmounts the user menu and every
    // exit with it. Needs its own. (`signOut` also purges the persisted boot cache.)
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-destructive">Failed to load workspace</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    );
  }
  // The loading gate above clears once the project url has resolved (it's part
  // of `isLoading`), so `projectUrl` is non-null by here.
  invariant(projectUrl, "project url should be resolved");

  const ctx: WorkspaceContextValue = {
    identity,
    authorEmail,
    author,
    doc,
    projectUrl,
    commands,
    pinned,
    files: doc.files,
    playground,
    apps,
    recentApps,
    openFile,
    closeFile,
    activeFile,
    openHistory,
    fileMutated,
    editorTabs,
    closeEditorTab,
    closeAllEditorTabs,
    openApp,
    openChat,
    newChat,
    chatInScope,
    openFilePanel: () => revealPanel("files"),
    pickUploadFiles: handleUploadClick,
    showHiddenFiles,
    toggleHiddenFiles,
    compact,
  };

  // The project list — four tabs (Files, Pins, Chats, Apps) plus a footer.
  // Shared between the wide shell's panel and the overlay Sheet, so its body
  // isn't a Sheet-only component. Only the header differs: beside the rail
  // the tab is already chosen there, so the panel names it and offers the
  // collapse; in the Sheet the tabs ride in the header (with right padding
  // that clears the Sheet's absolute close button).
  const sidebarBody = (persistent: boolean) => (
    <>
      {persistent ? (
        <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border pl-3 pr-2">
          <span className="text-sm font-medium">
            {workspacePanelTabLabel(fileTab)}
          </span>
          <RootCheckpointControls />
          <button
            type="button"
            onClick={() => panel.setOpen(false)}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Collapse panel"
            aria-label="Collapse project panel"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        </div>
      ) : (
        // Labels collapse to icons (via a container query on the Sheet) when
        // it is too narrow to hold all four plus the checkpoint controls.
        <div className="flex h-10 shrink-0 flex-row items-center justify-between gap-2 border-b border-border px-3 pr-12 text-left">
          <div className="flex items-center gap-1">
            <FileListTab
              label="Files"
              icon={<FolderTree className="h-4 w-4" />}
              active={fileTab === "files"}
              onClick={() => setFileTab("files")}
            />
            <FileListTab
              label="Pins"
              icon={<Pin className="h-4 w-4" />}
              active={fileTab === "pins"}
              onClick={() => setFileTab("pins")}
            />
            <FileListTab
              label="Chats"
              icon={<MessageSquare className="h-4 w-4" />}
              active={fileTab === "chats"}
              onClick={() => setFileTab("chats")}
            />
            <FileListTab
              label="Apps"
              icon={<LayoutGrid className="h-4 w-4" />}
              active={fileTab === "apps"}
              onClick={() => setFileTab("apps")}
            />
          </div>
          <RootCheckpointControls />
        </div>
      )}

      <div className="flex-1 overflow-y-auto py-1">
        {fileTab === "files" ? (
          <>
            <div className="flex items-center justify-end gap-1 px-2 pb-1">
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={isUploading}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 sm:p-1"
                title="Upload files"
                aria-label="Upload files"
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileUp className="h-4 w-4" />
                )}
              </button>
              <NewFileButton />
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleUploadInputChange}
              />
            </div>
            {uploadError ? (
              <p className="px-3 pb-2 text-xs text-destructive">
                {uploadError}
              </p>
            ) : null}
            <div {...getDropRootProps({ className: "relative px-1" })}>
              <input {...getDropInputProps()} />
              {isDragActive ? (
                <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-primary bg-background/90 text-sm font-medium">
                  <FileUp className="h-6 w-6" />
                  <span>Drop files to upload</span>
                </div>
              ) : null}
              <FileTree
                files={treeFiles}
                showHidden={showHiddenFiles}
                edit={commands.edit}
                expansion={expansion}
                openFiles={editorTabs}
                onOpenFile={(p) => {
                  openFile(p);
                }}
                // A blob row is a link the browser opens, as in Studio's list:
                // no lookup after the click, so no popup to block.
                rowHref={({ inode }) => blobHref(inode)}
                activeFile={activeFile}
                // Roomier rows on touch (input device, not screen size); tight
                // rows where a fine pointer makes small targets fine.
                rowClassName="py-1 pointer-coarse:py-2.5"
                // A directory that IS an App root renders as one opaque entry
                // (its display name + icon; click launches) — the App's
                // internals aren't browsable here, same as a macOS bundle.
                decorateEntry={({ path, isDir }) => {
                  if (!isDir) return null;
                  const entry = apps.entries.find((e) => e.path === path);
                  if (!entry) return null;
                  return {
                    label: entry.displayName,
                    icon: (
                      <AppIcon icon={entry.icon} className="h-4 w-4 shrink-0" />
                    ),
                    onOpen: () => openApp(entry.path),
                  };
                }}
                rowAction={({ path, isDir }) => (
                  <FileActionsMenu path={path} isDir={isDir} />
                )}
              />
            </div>
          </>
        ) : fileTab === "pins" ? (
          <PinnedList
            paths={pinned.paths}
            activeFile={activeFile}
            onOpen={(p) => {
              openFile(p);
            }}
            onUnpin={(p) => pinned.togglePin(p)}
          />
        ) : fileTab === "apps" ? (
          <AppsList apps={apps.entries} onOpen={openApp} />
        ) : (
          <>
            <div className="flex items-start justify-between gap-2 px-2 pb-1">
              <p className="text-xs leading-snug text-muted-foreground">
                Private to you and Heath.
              </p>
              <button
                type="button"
                onClick={newChat}
                disabled={!online}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 sm:p-1"
                title={
                  online ? "New chat" : "New chats are unavailable when offline"
                }
                aria-label={
                  online ? "New chat" : "New chats are unavailable when offline"
                }
              >
                <MessageSquarePlus className="h-4 w-4" />
              </button>
            </div>
            <ChatsList
              activeChatId={routeChatId}
              onOpen={openChat}
              onSaveToWorkspace={handleSaveSideChat}
              onAfterDelete={(id) => {
                // If the deleted chat is the one on screen, close it.
                if (routeChatId === id) goHome();
                else if (sideChatId === id) closeChatPane();
              }}
            />
          </>
        )}
      </div>

      {/* Footer: the sync/liveness dot + tenant switcher (current tenant for
          now) + the tenant settings entrypoint, moved here from the user menu
          because these settings are tenant-scoped, not personal. On mobile the
          user menu also lives here (no header to hold the avatar). */}
      <div className="flex shrink-0 items-center gap-2 border-t border-border px-4 pt-2 pb-edge">
        {compact ? (
          <div className="shrink-0">
            <UserMenu
              email={authorEmail}
              name={authorName}
              image={authorImage}
              onSignOut={() => signOut()}
            />
          </div>
        ) : null}
        <SyncIndicator />
        <TenantPicker current={identity.tenant} />
        <Link
          to="/settings"
          search={chatInScope ? { chat: chatInScope } : {}}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Settings"
          aria-label="Settings"
        >
          <Settings className="h-4 w-4" />
        </Link>
      </div>
    </>
  );

  // The header's leading button: beside the rail it toggles the panel; on a
  // narrower shell it opens the Sheet. Same cell either way, sized to the
  // rail so the icon lands on the rail's centre line.
  const panelShown = isWide && panel.open;
  const togglePanel = isWide
    ? () => panel.setOpen(!panel.open)
    : () => setFilesOpen(true);
  const PanelIcon = panelShown ? PanelLeftClose : PanelLeftOpen;

  // The sandbox registry needs both `playground` and `doc` to mount its
  // iframe; until both are ready we render the workspace without a
  // SandboxProvider — the Skills route will show a "waiting" state instead
  // of crashing.
  const tree = (
    <div ref={shellRef} className="flex h-dvh flex-col overflow-hidden">
      {!compact ? (
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border pr-4">
          <div
            className={cn(
              "flex shrink-0 items-center justify-center",
              WORKSPACE_RAIL_WIDTH,
            )}
          >
            <button
              type="button"
              onClick={togglePanel}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
                panelShown && "bg-muted text-foreground",
              )}
              aria-pressed={isWide ? panel.open : undefined}
              title={panelShown ? "Hide panel" : "Show panel"}
              aria-label={
                panelShown ? "Hide project panel" : "Show project panel"
              }
            >
              <PanelIcon className="h-4 w-4" />
            </button>
          </div>
          <h1 className="font-serif text-xl font-semibold tracking-tight">
            <Link to="/" className="hover:opacity-80">
              Hearth
            </Link>
          </h1>
          <div className="flex min-w-0 flex-1 justify-center">
            <CommandPalette variant="pill" />
          </div>
          {handle ? (
            <PresenceStack
              handle={handle as DocHandle<unknown>}
              self={presenceSelf}
            >
              <UserMenu
                email={authorEmail}
                name={authorName}
                image={authorImage}
                color={presenceSelf.color}
                onSignOut={() => signOut()}
              />
            </PresenceStack>
          ) : (
            <UserMenu
              email={authorEmail}
              name={authorName}
              image={authorImage}
              color={presenceSelf.color}
              onSignOut={() => signOut()}
            />
          )}
          <InstallButton />
          <button
            type="button"
            onClick={newChat}
            disabled={!online}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-50"
            title={
              online ? "New chat" : "New chats are unavailable when offline"
            }
            aria-label={
              online ? "New chat" : "New chats are unavailable when offline"
            }
          >
            <MessageSquarePlus className="h-4 w-4" />
            <span>New chat</span>
          </button>
        </header>
      ) : null}
      {/* The compact layout has no header — each view folds an `OpenPanelButton`
          into its own title bar to open the file list (the user menu lives in
          that panel's footer). Presence has no header to live in when compact,
          so the peer stack floats above the bottom toolbar — and only when
          someone else is actually here. */}
      {compact && handle ? (
        <PresenceStack
          handle={handle as DocHandle<unknown>}
          self={presenceSelf}
          floating
          // Keep broadcasting while in a chat (the dock owns the bottom), just
          // hide the cluster so it doesn't overlap the composer.
          hidden={!!chatInScope}
        />
      ) : null}
      <div className="flex flex-1 overflow-hidden">
        {/* On wide screens the project list is an icon rail with a panel
            beside it rather than a toggled overlay — there's room for files +
            a full-width document + the chat pane all at once, so the Sheet
            (below) doesn't render. The rail is always on screen; the panel
            collapses to it, never to nothing. Rail + panel together span
            SIDEBAR_WIDTH, so the layout math is what it was. */}
        {isWide ? (
          <WorkspaceRail
            tab={fileTab}
            open={panel.open}
            onSelect={revealPanel}
            onClose={() => panel.setOpen(false)}
          />
        ) : null}
        {panelShown ? (
          <aside className="flex w-68 shrink-0 flex-col border-r border-border">
            {sidebarBody(true)}
          </aside>
        ) : null}
        <main
          className={cn(
            "flex flex-col overflow-hidden",
            // When a chat fills the page, its surface takes over this row (see
            // the chat below). Hide the route content rather than unmounting
            // it, so the file/app the user came from stays warm on close.
            isChatFullPage ? "hidden" : "flex-1",
          )}
        >
          <Outlet />
        </main>
        {/* One persistent <ChatSurface> for the in-scope chat across every
            surface — full page, desktop side pane, and (mode="dock") the
            mobile bottom sheet. Keyed by chatInScope and kept at this one
            stable tree slot, so switching surfaces only changes its `mode`:
            the agent connection and message list survive, instead of each
            surface owning a separate doc subscription that splits pending state until
            a refresh. The surface owns its own container/positioning. */}
        {chatInScope && chatMode ? (
          // No fallback: the surface owns its own container/positioning, and
          // the chunk is precached — the gap is imperceptible.
          <Suspense fallback={null}>
            <ChatSurface
              key={chatInScope}
              chatId={chatInScope}
              mode={chatMode}
              openFilePath={activeFile}
              autoFocus={chatInScope === focusOnMountChatId}
              onBack={isChatFullPage ? goHome : closeChatPane}
              onSaveToWorkspace={handleSaveSideChat}
              onDelete={isChatFullPage ? goHome : closeChatPane}
            />
          </Suspense>
        ) : null}
      </div>

      {/* Mobile bottom toolbar — the command palette dominates it, with the
          "New chat" primary button to its right. It's a normal bottom flex
          child (not fixed), so content sits above it without overlap. Hidden
          whenever a chat is in scope: the dock / full-page chat owns the bottom
          of the screen and brings its own composer. */}
      {compact && !chatInScope ? (
        <div className="flex shrink-0 items-center gap-2 border-t border-border px-4 pt-2 pb-edge">
          <CommandPalette variant="bar" />
          <button
            type="button"
            onClick={newChat}
            disabled={!online}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-50"
            title={
              online ? "New chat" : "New chats are unavailable when offline"
            }
            aria-label={
              online ? "New chat" : "New chats are unavailable when offline"
            }
          >
            <MessageSquarePlus className="h-5 w-5" />
          </button>
        </div>
      ) : null}

      {!isWide ? (
        <Sheet open={filesOpen} onOpenChange={setFilesOpen}>
          <SheetContent
            side="left"
            className="@container flex w-[85vw] flex-col gap-0 p-0 sm:max-w-lg"
          >
            <SheetTitle className="sr-only">Project files</SheetTitle>
            {sidebarBody(false)}
          </SheetContent>
        </Sheet>
      ) : null}

      {pendingOverwrite ? (
        <OverwriteConfirm
          conflicts={pendingOverwrite.conflicts}
          onConfirm={handleConfirmOverwrite}
          onCancel={cancelOverwrite}
        />
      ) : null}
    </div>
  );

  return (
    <WorkspaceContext.Provider value={ctx}>
      {playground ? (
        <IntegrationConnectProvider>
          {/* App permission asks raised by a parked agent tool call. Sits above
              the chat surfaces so the interceptor that registers an ask and the
              card that answers it share one context — same placement, and same
              reason, as the integration connect provider. */}
          <AppConsentAsksProvider>
            <SandboxProvider
              playground={playground}
              localBuiltins={identity.localBuiltins}
            >
              {tree}
            </SandboxProvider>
          </AppConsentAsksProvider>
        </IntegrationConnectProvider>
      ) : (
        tree
      )}
    </WorkspaceContext.Provider>
  );
}

/**
 * The Files panel's "new markdown file" button. A component rather than a
 * handler on the shell so it sits inside the workspace provider and can use the
 * shared file commands — the same ones the row menu and ⌘K run.
 */
function NewFileButton() {
  const { commands } = useWorkspace();
  return (
    <button
      type="button"
      onClick={() => commands.create(DOCUMENT_KIND)}
      className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground sm:p-1"
      title="New markdown file"
      aria-label="New markdown file"
    >
      <FilePlus className="h-4 w-4" />
    </button>
  );
}

interface UserMenuProps {
  email: string;
  name: string | null;
  image: string | null;
  /** Fallback background when there's no avatar image (so it stays opaque when stacked). */
  color?: string;
  onSignOut: () => void;
}

function UserMenu({ email, name, image, color, onSignOut }: UserMenuProps) {
  const label = name?.trim() || email;
  const initial =
    (name?.trim().charAt(0) || email.trim().charAt(0)).toUpperCase() || "?";
  const [imageFailed, setImageFailed] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const showImage = image && !imageFailed;
  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          type="button"
          className="flex items-center rounded-full text-sm text-muted-foreground hover:opacity-80"
          aria-label={`User menu for ${label}`}
          title={label}
        >
          {showImage ? (
            <img
              src={image}
              alt=""
              referrerPolicy="no-referrer"
              onError={() => setImageFailed(true)}
              className="h-8 w-8 rounded-full object-cover ring-2 ring-background"
            />
          ) : (
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium text-white ring-2 ring-background"
              style={{ backgroundColor: color ?? "var(--muted-foreground)" }}
            >
              {initial}
            </span>
          )}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={4}
            className="z-50 min-w-[12rem] rounded-md border border-border bg-popover p-1 shadow-md"
          >
            <div className="truncate px-2 py-1.5 text-xs text-muted-foreground">
              {label}
            </div>
            <DropdownMenu.Separator className="-mx-1 my-1 h-px bg-border" />
            <DropdownMenu.Item asChild>
              <Link
                to="/me"
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
              >
                <UserRound className="h-4 w-4" /> Profile
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
              onSelect={() => setFeedbackOpen(true)}
            >
              <MessageSquarePlus className="h-4 w-4" /> Send feedback
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
              onSelect={onSignOut}
            >
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {/* Rendered as a sibling of the menu, not a child: a Dialog opened from a
          menu item while the menu is closing otherwise races Radix's focus
          restore (stuck `pointer-events: none` / focus yanked off the textarea). */}
      <SendFeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}

/**
 * Sidebar-footer workspace switcher. Shows the current tenant and, on click, a
 * menu of the user's *other* tenants, plus a "Create new space" entry that opens
 * the tenant-free `/welcome/new` claim flow. A user in a single space sees only
 * "Create new space".
 *
 * Every entry here leaves this origin, so every entry goes through
 * `/api/auth/enter`, which hands the identity you're signed in as *here* to the
 * destination — another workspace, or the tenant-free apex for "create new
 * space". Session cookies are host-only (so two Google accounts in one browser
 * keep separate workspaces), and this menu lists exactly the spaces the current
 * account belongs to; switching, or claiming a new space, must therefore arrive
 * as the account you did it from, not as whatever that origin last held.
 */
function TenantPicker({ current }: { current: string }) {
  const { data: tenants } = useTenants();
  const currentName =
    tenants?.find((t) => t.slug === current)?.name?.trim() || current;
  const others = (tenants ?? []).filter((t) => t.slug !== current);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        type="button"
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted sm:py-1.5"
        title={`Workspace: ${currentName}`}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {currentName}
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          side="top"
          sideOffset={4}
          className="z-50 min-w-[14rem] rounded-md border border-border bg-popover p-1 shadow-md"
        >
          {/* Real anchors (via Radix `asChild`) rather than JS navigation, so a
              switch survives popup blockers and honors cmd/middle-click. Another
              space opens in a new tab (the current workspace stays put); "Create
              new space" navigates in place. */}
          {others.map((t) => (
            <DropdownMenu.Item key={t.slug} asChild>
              <a
                href={enterPath({ tenant: t.slug })}
                target="_blank"
                rel="noreferrer"
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {t.name?.trim() || t.slug}
                </span>
              </a>
            </DropdownMenu.Item>
          ))}
          {others.length > 0 ? (
            <DropdownMenu.Separator className="-mx-1 my-1 h-px bg-border" />
          ) : null}
          <DropdownMenu.Item asChild>
            <a
              href={enterPath({ next: "/welcome/new" })}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground outline-none hover:bg-muted focus:bg-muted"
            >
              <Plus className="h-4 w-4" /> Create new space
            </a>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function FileListTab({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm transition-colors sm:py-1.5",
        active
          ? "bg-muted font-medium text-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
      aria-pressed={active}
      title={label}
    >
      {icon}
      {/* Labels collapse to icons when the panel itself (not the viewport) is
          narrower than @md — too tight to hold all four tabs plus the
          checkpoint controls, otherwise they'd overflow under the close button.
          The sheet is sized a step past @md (sm:max-w-lg) so its labels clear
          the threshold with margin rather than sitting on the edge. */}
      <span className="hidden @md:inline">{label}</span>
    </button>
  );
}

interface PinnedListProps {
  paths: string[];
  activeFile: string | null;
  onOpen: (path: string) => void;
  onUnpin: (path: string) => void;
}

function PinnedList({ paths, activeFile, onOpen, onUnpin }: PinnedListProps) {
  if (paths.length === 0) {
    return (
      <p className="px-3 py-2 text-sm text-muted-foreground">No pins yet</p>
    );
  }
  return (
    <ul className="px-1">
      {paths.map((path) => {
        const isActive = activeFile === path;
        return (
          <li key={path} className="group flex items-center">
            <button
              type="button"
              onClick={() => onOpen(path)}
              className={
                "flex-1 truncate rounded-md px-2 py-1 text-left text-sm pointer-coarse:py-2.5 " +
                (isActive
                  ? "bg-muted font-medium text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground")
              }
              title={path}
            >
              {basename(path)}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUnpin(path);
              }}
              // Touch has no hover, so the unpin affordance stays visible on
              // mobile and only fades-until-hover on pointer devices.
              className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground sm:p-1 sm:opacity-0 sm:group-hover:opacity-100"
              title={`Unpin ${path}`}
              aria-label={`Unpin ${path}`}
            >
              <PinOff className="h-3 w-3" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

interface AppsListProps {
  apps: AppIndexEntry[];
  /** Open an App by its directory path (the launcher identity). */
  onOpen: (path: string) => void;
}

function AppsList({ apps, onOpen }: AppsListProps) {
  const launchable = launchableApps(apps);

  const resync = useBuiltinResync();

  return (
    <div>
      <div className="flex items-center justify-end px-2 pb-1">
        <button
          type="button"
          onClick={() => resync.mutate()}
          disabled={resync.isPending}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-xs text-muted-foreground hover:text-foreground disabled:opacity-60"
          title="Check for built-in app updates"
        >
          <RefreshCw
            className={cn("h-3 w-3", resync.isPending && "animate-spin")}
          />
          Check for updates
        </button>
      </div>
      {launchable.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">No apps yet</p>
      ) : (
        <ul className="px-1">
          {launchable.map((app) => (
            <li key={app.path}>
              <button
                type="button"
                onClick={() => onOpen(app.path)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted hover:text-foreground pointer-coarse:py-2.5"
                title={app.displayName}
              >
                <AppIcon icon={app.icon} className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  {app.displayName}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? path : path.slice(i + 1);
}

interface OverwriteConfirmProps {
  conflicts: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

function OverwriteConfirm({
  conflicts,
  onConfirm,
  onCancel,
}: OverwriteConfirmProps) {
  const single = conflicts.length === 1;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-[420px] max-w-[90vw] rounded-lg border border-border bg-background p-5 shadow-xl">
        <p className="font-medium">
          {single ? "Overwrite existing file?" : "Overwrite existing files?"}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          {single
            ? `${conflicts[0]} already exists. Uploading will replace it.`
            : `${conflicts.length} files already exist and will be replaced: ${conflicts.join(", ")}.`}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>
  );
}

interface ChatsListProps {
  activeChatId: string | null;
  onOpen: (id: string) => void;
  onSaveToWorkspace: (
    chatId: string,
    title: string,
    messages: ChatMessage[],
  ) => Promise<void> | void;
  onAfterDelete: (chatId: string) => void;
}

function ChatsList({
  activeChatId,
  onOpen,
  onSaveToWorkspace,
  onAfterDelete,
}: ChatsListProps) {
  const list = useChatList();
  if (list.isLoading) {
    return (
      <div className="flex flex-col gap-1.5 px-3 py-2">
        <Skeleton className="h-7 w-full" />
        <Skeleton className="h-7 w-5/6" />
        <Skeleton className="h-7 w-2/3" />
      </div>
    );
  }
  if (list.error) {
    return (
      <p className="px-3 py-2 text-sm text-destructive">Failed to load chats</p>
    );
  }
  if (!list.data || list.data.length === 0) {
    return (
      <p className="px-3 py-2 text-sm text-muted-foreground">No chats yet</p>
    );
  }
  return (
    <ul className="px-1">
      {list.data.map((c) => {
        const isActive = activeChatId === c.id;
        return (
          <li key={c.id}>
            {/* The hover/active background lives on the whole row (button +
                action slot), not just the button — otherwise the trailing
                menu sits in an un-highlighted gap and its hover circle floats
                oddly beside a highlighted chat. */}
            <div
              className={
                "group relative flex items-center rounded-md pr-1 " +
                (isActive
                  ? "bg-muted font-medium text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground")
              }
            >
              <button
                type="button"
                onClick={() => onOpen(c.id)}
                className="flex min-w-0 flex-1 px-2 py-1.5 text-left pointer-coarse:py-2.5"
                title={c.title ?? "New chat"}
              >
                <span className="line-clamp-1 text-sm">
                  {c.title ?? "New chat"}
                </span>
              </button>
              {/* Reveal-on-hover for pointer devices; always visible where
                  there's no hover (touch), since it's otherwise unreachable. */}
              <div className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                <ChatActionsMenu
                  chatId={c.id}
                  title={c.title}
                  onSaveToWorkspace={onSaveToWorkspace}
                  onAfterDelete={onAfterDelete}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// Fixed widths of the two side columns (kept in sync with the layout:
// ChatSurface's `panel` frame is `w-[420px]`; the project list is the `w-12`
// rail plus its `w-68` panel). Used to decide, as the shell narrows, which
// column no longer fits.
const CHAT_PANEL_WIDTH = 420;
const SIDEBAR_WIDTH = 48 + 272;
// The narrowest the main content is allowed to get before we give up the next
// side column (dock the chat, then unpin the sidebar) rather than squeezing it.
const CONTENT_MIN_WIDTH = 640;
// Below this *shell* width the chrome goes compact (single column, no global
// header) — a genuinely phone-sized shell, independent of any open chat.
const COMPACT_MAX_WIDTH = 768;

// Track a DOM element's content-box width via ResizeObserver — the
// container-query equivalent for JS-driven layout decisions. Returns a callback
// ref to attach and the latest measured width (seeded from the viewport so the
// first paint is close before the observer fires).
function useElementWidth(): [(el: HTMLElement | null) => void, number] {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : COMPACT_MAX_WIDTH,
  );
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLElement | null) => {
    observerRef.current?.disconnect();
    if (!el) {
      observerRef.current = null;
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (typeof measured === "number") setWidth(measured);
    });
    // ResizeObserver delivers an initial observation before the first paint, so
    // the viewport-seeded width just covers that gap — no manual measure here.
    observer.observe(el);
    observerRef.current = observer;
  }, []);
  return [ref, width];
}
